import { useState, useRef, useEffect } from "react";
import {
  reportTemplates as mockReportTemplates,
  reportRecords as mockReportRecords,
  reportStats as mockReportStats,
} from "@/mocks/reports";
import { reportSamples as mockReportSamples, type ReportSample } from "@/mocks/reportSamples";
import { useApi } from "@/hooks/useApi";

type ReportTab = "templates" | "records" | "preview";

function StarRating({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-sm ${
            i < Math.floor(score / 20)
              ? "ri-star-fill text-amber-400"
              : "ri-star-fill text-gray-200"
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-semibold text-gray-700">{score}</span>
    </div>
  );
}

function StatCard({ icon, value, label, color }: { icon: string; value: string; label: string; color: string }) {
  return (
    <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <div className="text-lg font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function ReportPreview({ sample, onClose }: { sample: ReportSample; onClose: () => void }) {
  const [activeSection, setActiveSection] = useState("overview");
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const sections = [
    { key: "overview", label: "概览", icon: "ri-layout-grid-line" },
    { key: "timeline", label: "足迹", icon: "ri-route-line" },
    { key: "qa", label: "问答", icon: "ri-question-answer-line" },
    { key: "photos", label: "相册", icon: "ri-image-line" },
  ];

  const formatDuration = (min: number) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}小时${m > 0 ? `${m}分钟` : ""}`;
  };

  const handleNavClick = (key: string) => {
    const el = sectionRefs.current[key];
    if (el && contentRef.current) {
      const containerTop = contentRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const offset = elTop - containerTop - 12;
      contentRef.current.scrollBy({ top: offset, behavior: "smooth" });
      setActiveSection(key);
    }
  };

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { root: container, threshold: 0.3 }
    );

    sections.forEach((s) => {
      const el = sectionRefs.current[s.key];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100">
              <img src={sample.avatar} alt={sample.visitorName} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900">{sample.visitorName} · 旅游报告</div>
              <div className="text-xs text-gray-500">{sample.template} · {sample.ship} · {sample.tripDate}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Section Nav - sticky */}
        <div className="flex border-b border-gray-100 overflow-x-auto flex-shrink-0 bg-white">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => handleNavClick(s.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeSection === s.key
                  ? "border-teal-600 text-teal-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={s.icon} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Content - all sections in one scrollable area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto p-5">
          {/* Overview */}
          <section id="overview" ref={(el) => { sectionRefs.current.overview = el; }} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-layout-grid-line text-teal-600" />
              <span className="text-sm font-semibold text-gray-900">旅行概览</span>
            </div>
            <div className="space-y-5">
              {/* Cover Card */}
              <div className="relative rounded-xl overflow-hidden">
                <div className="w-full h-64 bg-gray-200">
                  <img src={sample.tripPhoto} alt="旅行封面" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-end gap-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow-lg bg-white">
                      <img src={sample.avatar} alt={sample.visitorName} className="w-full h-full object-cover" />
                    </div>
                    <div className="text-white">
                      <div className="text-lg font-bold">{sample.visitorName} 的三峡之旅</div>
                      <div className="text-sm text-white/80">{sample.tripDate} · {sample.ship} · {sample.tripBatch}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard
                  icon="ri-time-line"
                  value={formatDuration(sample.totalTime)}
                  label="游览时长"
                  color="bg-teal-50 text-teal-600"
                />
                <StatCard
                  icon="ri-map-pin-2-line"
                  value={`${sample.scenesVisited}个`}
                  label="打卡景点"
                  color="bg-amber-50 text-amber-600"
                />
                <StatCard
                  icon="ri-question-answer-line"
                  value={`${sample.questionCount}次`}
                  label="AI问答"
                  color="bg-blue-50 text-blue-600"
                />
                <StatCard
                  icon="ri-user-smile-line"
                  value={`${sample.faceswapCount}张`}
                  label="换脸照片"
                  color="bg-rose-50 text-rose-500"
                />
              </div>

              {/* Trip Highlight */}
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-quote-text text-teal-600" />
                  <span className="text-sm font-semibold text-teal-700">旅行感言</span>
                </div>
                <p className="text-sm text-teal-800 leading-relaxed">{sample.tripHighlight}</p>
              </div>

              {/* Score */}
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-lg">
                      {sample.satisfactionScore}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">满意度评分</div>
                      <div className="text-xs text-gray-500">满分 100 分</div>
                    </div>
                  </div>
                  <StarRating score={sample.satisfactionScore} />
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section id="timeline" ref={(el) => { sectionRefs.current.timeline = el; }} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-route-line text-teal-600" />
              <span className="text-sm font-semibold text-gray-900">足迹地图</span>
            </div>
            <div className="space-y-4">
              <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
                {sample.sceneVisits.map((scene, idx) => (
                  <div key={idx} className="relative">
                    <div className="absolute -left-[29px] top-0 w-5 h-5 rounded-full bg-white border-2 border-teal-300 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-teal-500" />
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <i className={`${scene.icon} ${scene.color} text-sm`} />
                          <span className="text-sm font-semibold text-gray-900">{scene.name}</span>
                        </div>
                        <span className="text-xs text-gray-500">{scene.time}</span>
                      </div>
                      <span className="text-xs text-gray-500">停留 {scene.duration} 分钟</span>
                    </div>
                  </div>
                ))}
                <div className="relative">
                  <div className="absolute -left-[29px] top-0 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                    <i className="ri-flag-line text-white text-xs" />
                  </div>
                  <div className="text-xs text-teal-600 font-medium">结束行程</div>
                </div>
              </div>
            </div>
          </section>

          {/* QA */}
          <section id="qa" ref={(el) => { sectionRefs.current.qa = el; }} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-question-answer-line text-teal-600" />
              <span className="text-sm font-semibold text-gray-900">问答精选</span>
              <span className="text-xs text-gray-500 ml-1">共 {sample.qaList.length} 条</span>
            </div>
            <div className="space-y-3">
              {sample.qaList.map((qa, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 flex items-center gap-2">
                    <span className="text-xs font-medium text-teal-600">{qa.scene}</span>
                    <span className="text-xs text-gray-400">{qa.time}</span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <i className="ri-user-line text-gray-400 mt-0.5" />
                      <div className="text-sm font-medium text-gray-900">{qa.question}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-robot-2-line text-teal-500 mt-0.5" />
                      <div className="text-sm text-gray-600 leading-relaxed">{qa.answer}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Photos */}
          <section id="photos" ref={(el) => { sectionRefs.current.photos = el; }} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-image-line text-teal-600" />
              <span className="text-sm font-semibold text-gray-900">旅行照片墙</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <div className="w-full h-56 bg-gray-200 rounded-xl overflow-hidden">
                  <img src={sample.tripPhoto} alt="旅行封面" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="w-full h-40 bg-gray-200 rounded-xl overflow-hidden">
                <img
                  src="/assets/img/rpt-ph-2.jpg"
                  alt="景点照片"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-full h-40 bg-gray-200 rounded-xl overflow-hidden">
                <img
                  src="/assets/img/rpt-ph-3.jpg"
                  alt="景点照片"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-full h-40 bg-gray-200 rounded-xl overflow-hidden">
                <img
                  src="/assets/img/rpt-ph-4.jpg"
                  alt="游船照片"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-full h-40 bg-gray-200 rounded-xl overflow-hidden">
                <img
                  src="/assets/img/rpt-ph-5.jpg"
                  alt="大坝照片"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>



          <div className="h-4" />
        </div>

        {/* Bottom fixed area - QR Code Download */}
        <div className="flex-shrink-0 border-t border-gray-100 px-5 py-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                <div className="text-center">
                  <i className="ri-qr-code-line text-2xl text-gray-300" />
                  <div className="text-[9px] text-gray-400 mt-0.5">{sample.qrCode}</div>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">{sample.qrCode}</div>
                <div className="text-xs text-gray-500 mt-0.5">微信扫码下载完整 PDF 报告</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-download-2-line" />
                下载报告
              </button>
              <button className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-teal-600 hover:bg-teal-50 border border-gray-200 rounded-lg transition-colors">
                <i className="ri-share-forward-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportsPage() {
  const { data: reportTemplatesRaw } = useApi<typeof mockReportTemplates>("/data/reportTemplates", mockReportTemplates);
  const reportTemplates = reportTemplatesRaw.map((t, i) => ({
    ...t,
    preview: (t as { preview?: string }).preview || mockReportTemplates[i % mockReportTemplates.length]?.preview || "",
  }));
  const { data: reportRecords } = useApi<typeof mockReportRecords>("/data/reportRecords", mockReportRecords);
  const { data: reportStats } = useApi<typeof mockReportStats>("/data/reportStats", mockReportStats);
  const { data: reportSamples } = useApi<typeof mockReportSamples>("/data/reportSamples", mockReportSamples);

  const [activeTab, setActiveTab] = useState<ReportTab>("templates");
  const [searchQuery, setSearchQuery] = useState("");
  const [previewSample, setPreviewSample] = useState<ReportSample | null>(null);

  const filteredRecords = reportRecords.filter((r) => {
    return searchQuery === "" || r.visitorName.includes(searchQuery);
  });

  const statusConfig = {
    generated: { label: "已生成", color: "bg-emerald-100 text-emerald-700" },
    failed: { label: "失败", color: "bg-red-100 text-red-700" },
    expired: { label: "已过期", color: "bg-gray-100 text-gray-700" },
  };

  const findSampleByName = (name: string) => {
    return reportSamples.find((s) => s.visitorName === name);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">旅游报告</h1>
          <p className="text-sm text-gray-500 mt-1">报告模板管理与生成记录</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {[
          { label: "模板总数", value: reportStats.totalTemplates, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "今日生成", value: reportStats.generatedToday, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
          { label: "总下载数", value: reportStats.totalDownloads, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex gap-1 px-4 pt-3">
            {[
              { key: "templates" as const, label: "报告模板", icon: "ri-file-copy-line" },
              { key: "records" as const, label: "生成记录", icon: "ri-history-line" },
              { key: "preview" as const, label: "报告预览", icon: "ri-eye-line" },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {activeTab === "templates" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportTemplates.map((template) => (
                <div key={template.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="w-full h-72 bg-gray-100 overflow-hidden">
                    <img src={template.preview} alt={template.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900">{template.name}</h3>
                      <span className="text-xs text-gray-400">{template.coverStyle}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">IP装饰：{template.ipDecoration}</p>
                    <div className="space-y-1 mb-3">
                      {template.modules.map((mod) => (
                        <div key={mod.name} className="flex items-center gap-2">
                          <i className={`${mod.enabled ? "ri-check-line text-emerald-500" : "ri-close-line text-gray-300"} text-xs`} />
                          <span className="text-xs text-gray-600">{mod.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex-1 py-2 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors text-center whitespace-nowrap">
                        编辑模板
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <i className="ri-eye-line" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button className="border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center h-96 text-gray-400 hover:border-teal-300 hover:text-teal-600 transition-colors">
                <i className="ri-add-circle-line text-3xl mb-2" />
                <span className="text-sm">新增模板</span>
              </button>
            </div>
          )}

          {activeTab === "records" && (
            <div className="space-y-4">
              <div className="relative flex-1 min-w-56">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索游客姓名" className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">游客</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">船次</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">生成时间</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">下载次数</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">二维码</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredRecords.map((record) => {
                      const sample = findSampleByName(record.visitorName);
                      return (
                        <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-gray-900">{record.visitorName}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{record.ship}</span>
                            <span className="text-xs text-gray-400 block">{record.tripId}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-500">{record.generatedAt}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[record.status].color}`}>
                              {statusConfig[record.status].label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{record.downloadCount}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-mono text-gray-600">{record.qrCode}</span>
                              <div className="w-8 h-8 bg-white border border-gray-200 rounded flex items-center justify-center">
                                <i className="ri-qr-code-line text-gray-400 text-sm" />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  if (sample) setPreviewSample(sample);
                                }}
                                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                                  sample
                                    ? "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                    : "text-gray-200 cursor-not-allowed"
                                }`}
                                disabled={!sample}
                                title={sample ? "查看报告" : "暂无预览数据"}
                              >
                                <i className="ri-eye-line" />
                              </button>
                              <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                                <i className="ri-refresh-line" />
                              </button>
                              <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                                <i className="ri-links-line" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-eye-line text-teal-600" />
                <span className="text-sm font-semibold text-gray-900">报告预览案例</span>
                <span className="text-xs text-gray-500">选择一份游客报告查看完整内容</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportSamples.map((sample) => (
                  <button
                    key={sample.id}
                    onClick={() => setPreviewSample(sample)}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden text-left hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="w-full h-40 bg-gray-100 overflow-hidden">
                      <img src={sample.tripPhoto} alt={sample.visitorName} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 border border-gray-100">
                          <img src={sample.avatar} alt={sample.visitorName} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{sample.visitorName}</div>
                          <div className="text-xs text-gray-500">{sample.age}岁 · {sample.hometown}</div>
                        </div>
                      </div>
                      <div className="space-y-1.5 mb-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-ship-line text-gray-400" />
                          <span>{sample.ship}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-time-line text-gray-400" />
                          <span>{sample.tripDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-file-list-3-line text-gray-400" />
                          <span>{sample.template}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="flex items-center gap-1 text-teal-600">
                          <i className="ri-map-pin-2-line" />
                          {sample.scenesVisited}个景点
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1 text-blue-600">
                          <i className="ri-question-answer-line" />
                          {sample.questionCount}次问答
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1 text-rose-500">
                          <i className="ri-user-smile-line" />
                          {sample.faceswapCount}张换脸
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400 text-xs" />
                          <span className="text-xs font-semibold text-gray-700">{sample.satisfactionScore}</span>
                        </div>
                        <span className="text-xs text-teal-600 font-medium">点击预览完整报告 →</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewSample && (
        <ReportPreview
          sample={previewSample}
          onClose={() => setPreviewSample(null)}
        />
      )}
    </div>
  );
}

export default ReportsPage;